import { request } from './client.js';
import type {
  ActionPage,
  ActionRequestBody,
  ActionRequestResponse,
  ClientTenant,
  ResponseSettingsUpdate,
} from './types.js';

/**
 * Response actions (Story 19, design §9.7). Every call here lands in the
 * response layer's routes; nothing in the SPA names an action except to
 * carry it to these functions (tools/lints/check_no_response_actions.py).
 */

function alerts(clientTenantId: string): string {
  return `/tenants/${encodeURIComponent(clientTenantId)}/alerts`;
}

/**
 * Request an action on one of the alert's targets. 202 `requested` when the
 * record was written and the Lambda invoked (poll the alert detail for the
 * outcome); 200 `withheld` when a rail stopped it; 409
 * `acknowledgment_required` when a warning in `available_actions` was not
 * in `acknowledge`.
 */
export function requestAction(
  clientTenantId: string,
  alertRef: string,
  body: ActionRequestBody,
  idToken: string,
): Promise<ActionRequestResponse> {
  return request<ActionRequestResponse>(
    'POST',
    `${alerts(clientTenantId)}/${encodeURIComponent(alertRef)}/actions`,
    idToken,
    { body },
  );
}

/** The manifest's release for a succeeded, partial or test action. Same outcomes as `requestAction`. */
export function releaseAction(
  clientTenantId: string,
  alertRef: string,
  actionId: string,
  body: Pick<ActionRequestBody, 'note' | 'acknowledge'>,
  idToken: string,
): Promise<ActionRequestResponse> {
  return request<ActionRequestResponse>(
    'POST',
    `${alerts(clientTenantId)}/${encodeURIComponent(alertRef)}/actions/${encodeURIComponent(actionId)}/release`,
    idToken,
    { body },
  );
}

/** Client-wide action history, newest first. */
export function listResponseActions(
  clientTenantId: string,
  params: { cursor?: string; limit?: number },
  idToken: string,
): Promise<ActionPage> {
  return request<ActionPage>(
    'GET',
    `/tenants/${encodeURIComponent(clientTenantId)}/response/actions`,
    idToken,
    { query: { ...params } },
  );
}

/** platform_admin only: the Client's response settings. Partial updates; returns the Client. */
export function updateResponseSettings(
  clientTenantId: string,
  body: ResponseSettingsUpdate,
  idToken: string,
): Promise<ClientTenant> {
  return request<ClientTenant>(
    'PUT',
    `/tenants/${encodeURIComponent(clientTenantId)}/response-settings`,
    idToken,
    { body },
  );
}
